<template>
    <ClickAndBuysStruct>
        <span id="address-section" />
        <LoadingStruct :is-loading="loadingInfos">
            <Card
                v-if="selected"
                mode="default"
                is-dark
                is-round
                id="clickandbuy-struct">
                <LinkObject
                    v-if="!isHideBacklink && !filesToken"
                    :to="
                        computedGetClickAndBuyRootLink +
                        computedGetClickAndBuyQueryParam
                    ">
                    <Button is-round mode="default" is-max>
                        <Translate translation-key="back" />
                    </Button>
                </LinkObject>
                <hr v-if="!isHideBacklink && !filesToken" id="divider-area" />

                <InfoBox
                    v-if="paymentFailed"
                    mode="warning"
                    class="uk-margin-medium-bottom">
                    <Translate translation-key="order.payment.failed.message" />
                </InfoBox>

                <div class="uk-text-center">
                    <img
                        v-if="selected.mediaFileId"
                        :src="
                            apiUrl +
                            '/clickandbuys/' +
                            selected.id +
                            '/media/' +
                            selected.mediaFileId
                        "
                        class="clickandbuy-media" />
                </div>

                <div
                    class="clickandbuy-price uk-badge badge lunacart-theme-background lunacart-theme-color">
                    {{ getPrice(selected) }}
                </div>

                <div class="clickandbuy-title">
                    {{ selected.title }}
                </div>

                <div
                    v-if="selected.text"
                    class="clickandbuy-text"
                    v-html="selected.text" />

                <hr />

                <div v-if="filesToken">
                    <div id="files" class="section-title">
                        <Translate translation-key="files" />

                        <div class="uk-margin-top" />
                        <div class="uk-grid-small" uk-grid>
                            <div v-for="file in files" :key="'cbf' + file">
                                <span v-on:click="actionDownloadFile(file)">
                                    <Button
                                        with-class="lunacart-theme-background lunacart-theme-background-hover lunacart-theme-color"
                                        is-large
                                        is-round
                                        :is-loading="filesLoading[file]"
                                        mode="primary">
                                        <Icon
                                            class="uk-margin-small-right"
                                            icon="cloud-download" />

                                        {{ file }}
                                    </Button>
                                </span>
                            </div>
                        </div>

                        <div v-if="filesTokenErr" class="uk-margin-bottom" />
                        <ErrorMessage
                            :custom-errors="[
                                {
                                    code: 401,
                                    message: $t('invalid.link'),
                                },
                                {
                                    code: 403,
                                    message: $t('invalid.link'),
                                },
                            ]"
                            :error="filesTokenErr" />
                    </div>
                </div>
                <div v-else>
                    <div
                        v-if="
                            selected.variants && selected.variants.length > 0
                        ">
                        <div class="section-title">
                            <Translate translation-key="options" />
                        </div>

                        <div class="uk-grid uk-child-width-1-1" uk-grid>
                            <div
                                v-for="(
                                    variant, variantKey
                                ) in selected.variants"
                                :key="variant.id">
                                <div class="uk-margin-small-bottom paper-text">
                                    {{ variant.name }}
                                </div>

                                <div
                                    v-if="variant.type === 'select'"
                                    class="uk-grid-small uk-child-width-expand@s uk-child-width-1-2"
                                    uk-grid>
                                    <div
                                        v-for="option in variant.options"
                                        :key="variant.id + option.name">
                                        <span
                                            v-on:click="
                                                selectVariantOption(
                                                    variantKey,
                                                    option.name
                                                )
                                            ">
                                            <LabelSelect
                                                :isDisabled="orderSuccess"
                                                class="lunacart-theme-selected"
                                                fill-selected
                                                is-round
                                                :is-selected="
                                                    variant.selected ===
                                                    option.name
                                                ">
                                                <div
                                                    class="variant-option uk-text-center">
                                                    {{ option.name }}
                                                </div>
                                                <div
                                                    class="uk-text-center paper-price">
                                                    <span
                                                        v-if="
                                                            option.price &&
                                                            option.price !== 0
                                                        ">
                                                        {{
                                                            getNumberWithCurrency(
                                                                option.price,
                                                                true
                                                            )
                                                        }}
                                                    </span>
                                                    <span v-else>
                                                        <Translate
                                                            translation-key="inclusive" />
                                                    </span>
                                                </div>
                                            </LabelSelect>
                                        </span>
                                    </div>
                                </div>

                                <div
                                    v-if="variant.type === 'checkbox'"
                                    class="uk-grid-small uk-child-width-1-1 uk-flex-bottom"
                                    uk-grid>
                                    <div
                                        v-for="option in variant.options"
                                        :key="variant.id + option.name">
                                        <SwitchButton
                                            swap
                                            :id="
                                                'clickandbuy-switch-option-' +
                                                (variant.id + option.name)
                                            "
                                            v-model="option.selected"
                                            :label="option.name"
                                            @input="setVariantOption">
                                            <span
                                                class="uk-text-center paper-price uk-margin-small-left uk-flex-bottom">
                                                <span
                                                    v-if="
                                                        option.price &&
                                                        option.price !== 0
                                                    ">
                                                    {{
                                                        getNumberWithCurrency(
                                                            option.price,
                                                            true
                                                        )
                                                    }}
                                                </span>
                                                <span v-else>
                                                    <Translate
                                                        translation-key="inclusive" />
                                                </span>
                                            </span>
                                        </SwitchButton>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="uk-margin-bottom" />
                        <hr />
                    </div>

                    <div v-if="selected && selected.isPriceVariable">
                        <div class="uk-margin-medium-bottom" />
                        <Translate
                            translation-key="variable.price.public.description" />

                        <div class="uk-margin-top" />
                        <CurrencyInput
                            @input="postOrderRequestPayment"
                            is-round
                            :is-disabled="orderSuccess"
                            v-model="priceField.value"
                            :icon="priceField.icon"
                            :label="priceField.label"
                            :name="priceField.name"
                            :options="computedCurrencyOptions"
                            :rules="priceField.rules"
                            :validate="trigger" />

                        <Translate
                            class="child-description"
                            translation-key="variable.price.minimum.price"
                            :parameter="{
                                x: getNumberWithCurrency(selected.price),
                            }" />

                        <div class="uk-margin-small-top" />
                        <div
                            class="uk-child-width-expand@s uk-child-width-1-2 uk-grid-small"
                            uk-grid>
                            <div v-for="priceTag in priceTags" :key="priceTag">
                                <span v-on:click="addPrice(priceTag)">
                                    <Button
                                        with-class="lunacart-theme-background lunacart-theme-background-hover lunacart-theme-color"
                                        class="uk-text-nowrap"
                                        is-round
                                        mode="theme"
                                        is-max>
                                        +
                                        {{ getNumberWithCurrency(priceTag) }}
                                    </Button>
                                </span>
                            </div>
                        </div>

                        <hr
                            class="uk-margin-small-top uk-margin-small-bottom" />

                        <div
                            class="uk-child-width-expand@s uk-child-width-1-2 uk-grid-small"
                            uk-grid>
                            <div v-for="priceTag in priceTags" :key="priceTag">
                                <span v-on:click="addPrice(priceTag * -1)">
                                    <Button
                                        class="uk-text-nowrap"
                                        is-round
                                        mode="default"
                                        is-max>
                                        -
                                        {{ getNumberWithCurrency(priceTag) }}
                                    </Button>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="
                            selected &&
                            isWithPrice &&
                            selected.multipleOrderable
                        ">
                        <div
                            v-if="
                                selected.shippingCostsOnce &&
                                selected.shippingFee &&
                                selected.shippingFee > 0.0
                            "
                            class="uk-margin-medium-bottom" />
                        <Translate
                            v-if="
                                selected.shippingCostsOnce &&
                                selected.shippingFee &&
                                selected.shippingFee > 0.0
                            "
                            translation-key="shippingCostsOnceDescription" />

                        <div class="uk-margin-top" />

                        <LabelWithError
                            :label="$t(amountField.label)"
                            :error="errors.first(amountField.name)" />
                        <div class="uk-grid-small" uk-grid>
                            <div class="uk-width-expand@s uk-width-1-1">
                                <TextField
                                    @input="postOrderRequestPayment"
                                    is-round
                                    :on-change="intvalAmount"
                                    is-label-hidden
                                    :disabled="orderSuccess"
                                    :is-disabled="orderSuccess"
                                    v-model="amountField.value"
                                    :icon="amountField.icon"
                                    :label="amountField.label"
                                    :name="amountField.name"
                                    :rules="amountField.rules"
                                    :type="amountField.type"
                                    :steps="amountField.steps"
                                    :validate="trigger" />
                            </div>

                            <div class="uk-width-small@s uk-width-1-2">
                                <span v-on:click="addAmount(1)">
                                    <Button
                                        :is-disabled="orderSuccess"
                                        with-class="uk-text-nowrap add-amount-button lunacart-theme-background lunacart-theme-background-hover lunacart-theme-color"
                                        is-round
                                        mode="info"
                                        is-max>
                                        + 1
                                    </Button>
                                </span>
                            </div>
                            <div class="uk-width-small@s uk-width-1-2">
                                <span v-on:click="addAmount(-1)">
                                    <Button
                                        :is-disabled="orderSuccess"
                                        with-class="uk-text-nowrap add-amount-button"
                                        is-round
                                        mode="default"
                                        is-max>
                                        - 1
                                    </Button>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="uk-margin-medium-bottom" />
                    <div v-if="false" class="section-title">
                        {{ $t('address') }}
                    </div>
                    <div class="uk-grid-small" uk-grid>
                        <div
                            v-for="(inputField, key) in contactFields"
                            :class="inputField.width"
                            :key="'i' + inputField.name">
                            <TextField
                                :disabled="orderSuccess"
                                is-round
                                v-model="inputField.value"
                                :autocomplete="inputField.autocomplete"
                                :autofocus="inputField.autofocus"
                                :field-key="key"
                                :hasPasswordMeter="inputField.hasPasswordMeter"
                                :icon="inputField.icon"
                                :is-optional="inputField.optional"
                                :label="inputField.label"
                                :name="inputField.name"
                                :rules="inputField.rules"
                                :type="inputField.type"
                                :validate="triggerContactFields"
                                @setValid="setValidContactFields"
                                :has-keyboard="isTerminalMode" />
                            <div
                                v-if="key < contactFields.length - 1"
                                class="uk-margin-small-bottom" />
                        </div>
                    </div>

                    <div v-if="isWithPrice">
                        <div class="uk-margin-small-bottom" />
                        <LabelWithError
                            :label="
                                $t('country') + ' (' + $t('optional') + ')'
                            " />
                        <form autocomplete="off">
                            <select
                                v-model="country"
                                name="country"
                                :disabled="
                                    getInfos.shippingCountries.length === 1 ||
                                    orderSuccess
                                "
                                class="uk-select uk-input-styled round">
                                <option
                                    v-for="option in getInfos.shippingCountries"
                                    :key="option"
                                    :value="option">
                                    {{ $t(option) }}
                                </option>
                            </select>
                        </form>
                    </div>

                    <div class="uk-margin-medium-bottom" />
                    <TextArea
                        :is-disabled="orderSuccess"
                        v-model="comments"
                        is-round
                        :placeholder="$t('comments')"
                        is-label-hidden
                        is-label-large
                        label="comments"
                        name="comments"
                        rules="max:4096" />

                    <div v-if="selected.price && selected.price > 0.0">
                        <div class="uk-margin-medium-bottom" />
                        <div
                            class="uk-grid-small uk-margin-small-medium-top total-costs-text"
                            uk-grid>
                            <div class="uk-width-expand" uk-leader>
                                <span
                                    v-if="selected.multipleOrderable"
                                    class="uk-badge badge lunacart-theme-background lunacart-theme-color">
                                    {{ amountField.value }}
                                </span>
                                {{ selected.title }}
                            </div>
                            <div>
                                {{ getNumberWithCurrency(computedBuyPrice) }}
                            </div>
                        </div>
                        <div
                            v-if="selected.variants"
                            class="uk-grid-small uk-margin-small-medium-top total-costs-text"
                            uk-grid>
                            <div class="uk-width-expand" uk-leader>
                                <span
                                    v-if="selected.multipleOrderable"
                                    class="uk-badge badge lunacart-theme-background lunacart-theme-color">
                                    {{ amountField.value }}
                                </span>

                                {{ $t('options') }}
                            </div>
                            <div>
                                {{
                                    getNumberWithCurrency(
                                        computedOptionsPrice() *
                                            amountField.value
                                    )
                                }}
                            </div>
                        </div>

                        <div
                            v-if="
                                selected.shippingFee &&
                                selected.shippingFee > 0.0
                            "
                            class="uk-grid-small uk-margin-small-medium-top total-costs-text"
                            uk-grid>
                            <div class="uk-width-expand" uk-leader>
                                <span
                                    v-if="selected.multipleOrderable"
                                    class="uk-badge badge lunacart-theme-background lunacart-theme-color">
                                    <span v-if="selected.shippingCostsOnce">
                                        1
                                    </span>
                                    <span v-else>
                                        {{ amountField.value }}
                                    </span>
                                </span>

                                {{ $t('shipping') }}
                            </div>
                            <div>{{ getPriceShipping(selected) }}</div>
                        </div>
                        <div
                            class="uk-grid-small uk-margin-small-medium-top total-costs-text"
                            uk-grid>
                            <div class="uk-width-expand" uk-leader>
                                {{ $t('total') }}
                            </div>
                            <div>{{ getPriceTotal(selected) }}</div>
                        </div>
                        <div class="vat">
                            {{
                                $t('vat.message', {
                                    vat: getNumberFormatted(getInfos.vat),
                                })
                            }}
                        </div>

                        <div v-if="!orderSuccess">
                            <div class="uk-margin-medium-bottom" />
                            <div
                                v-if="false"
                                class="section-title"
                                id="payment-method">
                                {{ $t('payment.method') }}
                            </div>

                            <stripe-element-payment
                                v-if="elementsOptions.clientSecret"
                                @element-ready="setPaymentRendered"
                                @element-change="onElementChange"
                                ref="paymentRef"
                                :pk="getInfos.stripe.key"
                                :elements-options="elementsOptions"
                                :confirm-params="confirmParams" />
                        </div>
                    </div>

                    <div v-if="!orderSuccess">
                        <div class="uk-margin-medium-bottom" />
                        <div v-if="false" class="section-title">
                            {{ $t('termsAndCondition.and.dataPrivacy') }}
                        </div>
                        <Checkbox
                            :disabled="orderSuccess"
                            is-round
                            v-model="confirmTerms"
                            :validate="triggerTerms"
                            label="termsAndConditions.confirm"
                            name="terms"
                            rules="required"
                            @setValid="setTermsValid" />
                    </div>

                    <div class="uk-margin-medium-bottom" />
                    <Button
                        is-round
                        :is-disabled="orderSuccess || paymentLoading"
                        :action="actionOrder"
                        :is-loading="orderLoading"
                        :text="$t(isWithPrice ? 'paid.buy' : 'Confirm')"
                        has-action
                        is-large
                        is-max
                        mode="success" />
                    <div v-if="orderError" class="uk-margin-medium-bottom" />
                    <ErrorMessage
                        :custom-errors="[
                            {
                                code: 408,
                                message: $t('session.timed.out'),
                            },
                        ]"
                        :error="orderError" />

                    <div v-if="paymentError" class="uk-margin-medium-bottom" />
                    <InfoBox
                        v-if="paymentError"
                        class="uk-animation-fade"
                        mode="danger">
                        <Translate translation-key="payment.denied.error" />
                    </InfoBox>

                    <div v-if="orderSuccess" class="uk-margin-medium-bottom" />
                    <SuccessMessage
                        withClasses="lunacart-theme-border"
                        without-animation
                        id="success"
                        :success="orderSuccess">
                        <Translate
                            v-if="isWithPrice"
                            translation-key="paid.order.success" />
                        <Translate
                            v-else
                            translation-key="free.order.success" />

                        <div class="uk-margin-medium-bottom" />

                        <Button
                            with-class="lunacart-theme-background lunacart-theme-color lunacart-theme-background-hover"
                            v-if="isHideBacklink"
                            has-action
                            :action="orderAgainReload"
                            is-round
                            mode="primary"
                            is-max
                            is-large>
                            <Translate translation-key="orderAgain" />
                        </Button>
                        <LinkObject
                            v-else
                            :to="
                                computedGetClickAndBuyRootLink +
                                computedGetClickAndBuyQueryParam
                            ">
                            <Button
                                with-class="lunacart-theme-background lunacart-theme-color lunacart-theme-background-hover"
                                is-round
                                mode="primary"
                                is-max
                                is-large>
                                <Translate translation-key="back.to.overview" />
                            </Button>
                        </LinkObject>

                        <hr class="uk-margin-top uk-margin-bottom" />

                        <Button
                            v-if="!isHideBacklink"
                            has-action
                            :action="orderAgainReload"
                            is-round
                            mode="default"
                            is-max
                            is-large>
                            <Translate translation-key="orderAgain" />
                        </Button>
                    </SuccessMessage>
                </div>
            </Card>

            <Modal
                :id="modalIdOrderSuccess"
                :title="$t('paid.order.success.title')">
                <SuccessMessage
                    without-animation
                    success
                    withClasses="lunacart-theme-border">
                    <Translate translation-key="paid.order.success" />
                </SuccessMessage>

                <div class="uk-margin-medium-bottom" />
                <Button
                    with-class="lunacart-theme-background lunacart-theme-background-hover lunacart-theme-color"
                    v-if="isHideBacklink"
                    has-action
                    :action="orderAgainReload"
                    is-round
                    mode="primary"
                    is-max
                    is-large>
                    <Translate translation-key="orderAgain" />
                </Button>
                <LinkObject
                    class="uk-modal-close"
                    v-else
                    :to="
                        computedGetClickAndBuyRootLink +
                        computedGetClickAndBuyQueryParam
                    ">
                    <Button
                        with-class="lunacart-theme-background lunacart-theme-color lunacart-theme-background-hover"
                        is-round
                        mode="primary"
                        is-max
                        is-large>
                        <Translate translation-key="back.to.overview" />
                    </Button>
                </LinkObject>
                <hr class="uk-margin-top uk-margin-bottom" />

                <Button
                    v-if="!isHideBacklink"
                    has-action
                    :action="orderAgainReload"
                    is-round
                    mode="default"
                    is-max
                    is-large>
                    <Translate translation-key="orderAgain" />
                </Button>
            </Modal>
        </LoadingStruct>
    </ClickAndBuysStruct>
</template>

<script>
import Button from '@/components/generic/Button.vue';
import Translate from '@/components/Translate.vue';
import Card from '@/components/generic/Card.vue';
import TextArea from '@/components/TextArea.vue';
import TextField from '@/components/generic/TextField.vue';
import LabelWithError from '@/components/generic/LabelWithError.vue';
import Checkbox from '@/components/generic/Checkbox.vue';
import axios from 'axios';
import {StripeElementPayment} from '@vue-stripe/vue-stripe';
import ErrorMessage from '@/components/generic/ErrorMessage.vue';
import {parse as uuidParse} from 'uuid';
import ClickAndBuysStruct from '@/views/clickandbuy/ClickAndBuysStruct.vue';
import LoadingStruct from '@/views/LoadingStruct.vue';
import LinkObject from '@/components/LinkObject.vue';
import SuccessMessage from '@/components/generic/SuccessMessage.vue';
import UIkit from 'uikit';
import InfoBox from '@/components/generic/InfoBox.vue';
import CurrencyInput from '@/components/generic/CurrencyInput.vue';
import LabelSelect from '../../components/LabelSelect.vue';
import SwitchButton from '../../components/generic/SwitchButton.vue';
import Vue from 'vue';
import Modal from '@/components/generic/Modal.vue';
import {i18n} from '@/main';
import Icon from '@/components/generic/Icon.vue';

export default {
    name: 'ClickAndBuy',
    components: {
        Icon,
        Modal,
        SwitchButton,
        LabelSelect,
        CurrencyInput,
        InfoBox,
        SuccessMessage,
        LinkObject,
        LoadingStruct,
        ClickAndBuysStruct,
        ErrorMessage,
        StripeElementPayment,
        Checkbox,
        LabelWithError,
        TextField,
        TextArea,
        Card,
        Translate,
        Button,
    },
    props: {
        clickAndBuyID: String,
    },
    data() {
        return {
            selected: null,

            trigger: 0,
            priceField: {
                value: '',
                valid: true,

                name: 'price',
                label: 'price',
                icon: 'tag',
                rules: 'required',
                type: 'number',
                steps: 0.01,
            },

            amountField: {
                value: 1,
                valid: true,

                name: 'amount',
                label: 'amount',
                icon: 'cart',
                rules: 'required|integer',
                type: 'number',
                steps: 1,
            },

            paymentFailed: false,

            priceTags: [5, 10, 50, 100],

            triggerContactFields: 0,
            contactFields: [
                {
                    value: '',
                    valid: false,

                    name: 'name',
                    label: 'name',
                    icon: 'user',
                    rules: 'required|max:320',
                    width: 'uk-width-1-2@m uk-width-1-1',
                },
                {
                    value: '',
                    valid: false,

                    name: 'email',
                    label: 'emailAddress',
                    icon: 'mail',
                    rules: 'required|emailV2',
                    width: 'uk-width-1-2@m uk-width-1-1',
                },

                {
                    value: '',
                    valid: true,

                    name: 'street',
                    label: 'street',
                    icon: 'home',
                    rules: 'max:320',
                    width: 'uk-width-1-1',
                },
                {
                    value: '',
                    valid: true,

                    name: 'zip',
                    label: 'zip',
                    icon: 'location',
                    rules: 'numeric|max:320',
                    width: 'uk-width-1-2@m uk-width-1-1',
                },
                {
                    value: '',
                    valid: true,

                    name: 'city',
                    label: 'city',
                    icon: 'world',
                    rules: 'max:320',
                    width: 'uk-width-1-2@m uk-width-1-1',
                },
            ],

            country: null,

            comments: '',

            confirmTerms: false,
            triggerTerms: 0,

            loadingInfos: true,

            modalIdOrderSuccess: 'clickandbuy-order-success',

            requestId: undefined,
            paymentLoading: false,

            paymentRendered: false,
            paymentCompleted: false,
            elementsOptions: {
                clientSecret: null,
                appearance: {
                    variables: {
                        colorPrimary: '#092633',
                        colorBackground: '#ffffff',
                        colorText: '#30313d',
                        colorDanger: '#df1b41',
                        borderRadius: '5px',
                    },
                },
            },
            confirmParams: {
                return_url:
                    window.location.href +
                    this.computedGetClickAndBuyRootLink +
                    this.computedGetClickAndBuyQueryParam,
            },

            orderLoading: false,
            orderError: null,
            orderSuccess: false,

            paymentError: null,

            filesToken: null,
            filesTokenErr: null,
            files: [],
            filesLoading: [],
        };
    },
    computed: {
        computedBuyPrice() {
            if (this.selected.isPriceVariable) {
                return this.priceField.value;
            }

            return this.selected.price * this.amountField.value;
        },
        isHideBacklink() {
            if (this.selected && this.selected.hidden) {
                return true;
            }

            if (!this.isEmbedded) {
                return false;
            }

            const hidebacklink = this.$route.query.hidebacklink;
            return hidebacklink === 'true';
        },
        isEmbedded() {
            const embedded = this.$route.query.embedded;
            return embedded === 'true';
        },
        computedCurrencyOptions() {
            let minPrice = 0.5;
            if (this.selected && this.selected.price) {
                minPrice = this.selected.price;
            }

            return {
                currency: this.getInfos.currency,
                currencyDisplay: 'symbol',
                valueRange: {
                    min: minPrice,
                },
                precision: 2,
                hideCurrencySymbolOnFocus: false,
                hideGroupingSeparatorOnFocus: false,
                hideNegligibleDecimalDigitsOnFocus: false,
                autoDecimalDigits: false,
                useGrouping: true,
                accountingSign: false,
            };
        },
        computedIsClickAndBuyFollow() {
            return this.$store.state.clickandbuyfollow;
        },
        computedOriginPath() {
            return window.location.origin + window.location.pathname;
        },
        isWithPrice() {
            return (
                this.selected &&
                this.selected.price &&
                this.selected.price > 0.0
            );
        },
    },
    mounted() {
        let self = this;
        let intervalId = setInterval(function () {
            const successElement =
                document.getElementById('clickandbuy-struct');
            if (
                !successElement ||
                !self.getInfos ||
                !self.getInfos.clickAndBuy
            ) {
                return;
            }

            clearInterval(intervalId);

            const paymentStatus = self.$route.query.redirect_status;
            if (
                paymentStatus &&
                (paymentStatus.includes('succeeded') ||
                    paymentStatus.includes('processing'))
            ) {
                UIkit.modal('#' + self.modalIdOrderSuccess).show();
            }
        }, 5);

        setInterval(function () {
            if (
                !self.getInfos ||
                !self.getInfos.clickAndBuy ||
                !self.getInfos.clickAndBuy.themeBackgroundColor
            ) {
                return;
            }

            if (
                self.elementsOptions.appearance.variables.colorPrimary !==
                self.getInfos.clickAndBuy.themeBackgroundColor
            ) {
                self.elementsOptions.appearance.variables.colorPrimary =
                    self.getInfos.clickAndBuy.themeBackgroundColor;
            }

            self.setLunacartThemeColors(
                self.getInfos.clickAndBuy.themeBackgroundColor,
                self.getInfos.clickAndBuy.themeColor
            );

            self.assertLabelSelectColors();
        }, 5);
    },
    methods: {
        assertLabelSelectColors() {
            let self = this;

            const elementsSelected = document.querySelectorAll(
                '.lunacart-theme-selected'
            );
            elementsSelected.forEach((el) => {
                if (el.classList.contains('selected')) {
                    el.style.setProperty(
                        'border-color',
                        self.getInfos.clickAndBuy.themeBackgroundColor,
                        'important'
                    );

                    el.style.setProperty(
                        'background-color',
                        self.getInfos.clickAndBuy.themeBackgroundColor,
                        'important'
                    );

                    el.style.setProperty(
                        'color',
                        self.getInfos.clickAndBuy.themeColor,
                        'important'
                    );
                } else {
                    if (
                        el.style.borderColor ===
                        self.hexToRgb(
                            self.getInfos.clickAndBuy.themeBackgroundColor
                        )
                    ) {
                        el.style.setProperty(
                            'border-color',
                            '#e5e5e5',
                            'important'
                        );
                    }

                    el.style.setProperty(
                        'background-color',
                        '#fff',
                        'important'
                    );

                    el.style.setProperty('color', '#121212', 'important');

                    el.addEventListener('mouseenter', () => {
                        el.style.setProperty(
                            'border-color',
                            '#949393',
                            'important'
                        );
                    });
                    el.addEventListener('mouseleave', () => {
                        el.style.setProperty(
                            'border-color',
                            '#e5e5e5',
                            'important'
                        );
                    });
                }
            });
        },
        orderAgainReload() {
            this.orderAgain(true);
        },
        orderAgain(reload) {
            const url = '/' + i18n.locale + '/lunacart/' + this.selected.id;

            if (reload) {
                window.location.href = url;
            } else {
                this.$router.push(url);
            }
        },
        computedOptionsPrice() {
            let price = 0;
            if (!this.selected.variants) {
                return price;
            }

            for (let i = 0, j = this.selected.variants.length; i < j; i++) {
                const variant = this.selected.variants[i];

                if (variant.type === 'checkbox') {
                    for (let k = 0, l = variant.options.length; k < l; k++) {
                        const option = variant.options[k];

                        if (option.selected && option.price) {
                            price += option.price;
                        }
                    }
                }

                if (variant.type === 'select') {
                    for (let k = 0, l = variant.options.length; k < l; k++) {
                        const option = variant.options[k];

                        if (option.name === variant.selected && option.price) {
                            price += option.price;
                            break;
                        }
                    }
                }
            }

            return price;
        },
        selectVariantOption(variantKey, optionName) {
            if (this.orderSuccess) {
                return;
            }

            Vue.set(this.selected.variants[variantKey], 'selected', optionName);
            this.$forceUpdate();

            this.postOrderRequestPayment();
        },
        setVariantOption() {
            this.$forceUpdate();
            this.postOrderRequestPayment();
        },
        addPrice(val) {
            let targetPrice = this.priceField.value;
            targetPrice += val;

            if (targetPrice < this.selected.price) {
                targetPrice = this.selected.price;
            }

            this.priceField.value = targetPrice;
            this.postOrderRequestPayment();
        },
        intvalAmount() {
            let value = parseInt(this.amountField.value);
            if (value < 1 || !value) {
                value = 1;
            }

            this.amountField.value = value;
        },
        addAmount(val) {
            if (this.orderSuccess) {
                return;
            }

            let targetPrice = this.amountField.value;
            targetPrice += val;

            if (targetPrice < 1) {
                targetPrice = 1;
            }

            this.amountField.value = targetPrice;
            this.postOrderRequestPayment();
        },
        handleScroll() {
            if (this.filesToken) {
                this.awaitScrollTo('files');
            } else if (!this.paymentFailed) {
                if (this.computedIsClickAndBuyFollow) {
                    this.awaitScrollTo('address-section');
                }
            } else {
                this.awaitScrollTo('divider-area');
            }
        },
        onElementChange(event) {
            this.paymentCompleted = event.complete;
        },
        actionOrder() {
            this.triggerTerms++;
            this.triggerContactFields++;

            if (!this.termsValid) {
                return;
            }

            for (let i = 0, j = this.contactFields.length; i < j; i++) {
                if (!this.contactFields[i].valid) {
                    return;
                }
            }

            const isPaid = this.selected.price > 0.0;

            if (isPaid && !this.paymentCompleted) {
                this.$refs.paymentRef.focus();
                return;
            }

            let platform = 'browser';
            if (this.isApp) {
                platform = 'app';
            }

            this.orderLoading = true;

            let street;
            if (this.contactFields[2]) {
                street = this.contactFields[2].value;
            }

            let zip;
            if (this.contactFields[3]) {
                zip = this.contactFields[3].value;
            }

            let city;
            if (this.contactFields[4]) {
                city = this.contactFields[4].value;
            }

            let country;
            if (this.country) {
                country = this.country;
            }

            let variablePrice = undefined;
            if (this.selected.isPriceVariable) {
                variablePrice = this.priceField.value;
            }

            let variants = [];
            if (this.selected.variants) {
                for (let i = 0, j = this.selected.variants.length; i < j; i++) {
                    const variant = this.selected.variants[i];

                    if (variant.type === 'checkbox') {
                        for (
                            let i = 0, j = variant.options.length;
                            i < j;
                            i++
                        ) {
                            const option = variant.options[i];

                            if (option.selected) {
                                variants.push({
                                    variantId: variant.id,
                                    optionName: option.name,
                                });
                            }
                        }
                    }

                    if (variant.type === 'select' && variant.selected) {
                        variants.push({
                            variantId: variant.id,
                            optionName: variant.selected,
                        });
                    }
                }
            }

            let http = axios.create();
            http.post(
                this.apiUrl + '/clickandbuys/' + this.selected.id + '/orders',
                {
                    name: this.contactFields[0].value,
                    email: this.contactFields[1].value,
                    comments: this.comments,
                    language: this.$i18n.locale,
                    platform: platform,

                    requestId: this.requestId,
                    street: street,
                    zip: zip,
                    city: city,
                    country: country,

                    variablePrice: variablePrice,

                    variants: variants,

                    amount: this.amountField.value,
                }
            )
                .then(async (response) => {
                    const self = this;
                    if (isPaid) {
                        try {
                            const result =
                                await this.$refs.paymentRef.stripe.confirmPayment(
                                    {
                                        elements:
                                            this.$refs.paymentRef.elements,
                                        confirmParams: {
                                            return_url:
                                                this.computedOriginPath.includes(
                                                    '/orders/'
                                                )
                                                    ? this.computedOriginPath
                                                    : this.computedOriginPath.endsWith(
                                                          '/'
                                                      )
                                                    ? this.computedOriginPath +
                                                      'orders/' +
                                                      response.data.orderId +
                                                      '/'
                                                    : this.computedOriginPath +
                                                      '/orders/' +
                                                      response.data.orderId +
                                                      '/',
                                        },
                                    }
                                );

                            if (result.error) {
                                self.orderLoading = false;
                                this.$refs.paymentRef.focus();

                                const link = this.computedOriginPath.includes(
                                    '/orders/'
                                )
                                    ? this.computedOriginPath
                                    : this.computedOriginPath.endsWith('/')
                                    ? this.computedOriginPath +
                                      'orders/' +
                                      response.data.orderId +
                                      '/?redirect_status=failed'
                                    : this.computedOriginPath +
                                      '/orders/' +
                                      response.data.orderId +
                                      '/?redirect_status=failed';

                                window.open(link, '_self').focus();
                            } else {
                                self.orderLoading = false;
                                self.orderError = null;
                            }
                        } catch (e) {
                            self.orderLoading = false;
                            this.$refs.paymentRef.focus();
                            this.paymentError = e;
                        }
                    } else {
                        this.orderError = null;
                        this.orderSuccess = true;
                        this.orderLoading = false;

                        this.awaitScrollTo('success');
                        UIkit.modal('#' + self.modalIdOrderSuccess).show();
                    }
                })
                .catch((err) => {
                    this.orderError = err;
                    this.orderLoading = false;
                });
        },
        awaitScrollTo(id) {
            let intervalId = setInterval(function () {
                const successElement = document.getElementById(id);
                if (!successElement) {
                    return;
                }

                clearInterval(intervalId);

                UIkit.scroll('#' + id, {offset: 0}).scrollTo('#' + id);
            }, 10);
        },
        postOrderRequestPayment(variablePrice = undefined) {
            if (this.selected.isPriceVariable) {
                variablePrice = this.priceField.value;
            }

            let variants = [];

            if (this.selected.variants) {
                for (let i = 0, j = this.selected.variants.length; i < j; i++) {
                    const variant = this.selected.variants[i];

                    if (variant.type === 'checkbox') {
                        for (
                            let i = 0, j = variant.options.length;
                            i < j;
                            i++
                        ) {
                            const option = variant.options[i];

                            if (option.selected) {
                                variants.push({
                                    variantId: variant.id,
                                    optionName: option.name,
                                });
                            }
                        }
                    }

                    if (variant.type === 'select' && variant.selected) {
                        variants.push({
                            variantId: variant.id,
                            optionName: variant.selected,
                        });
                    }
                }
            }

            this.paymentLoading = true;
            let http = axios.create();
            http.post(
                this.apiUrl + '/clickandbuys/' + this.selected.id + '/payment',
                {
                    requestId: this.requestId,
                    variablePrice: variablePrice,
                    variants: variants,

                    amount: this.amountField.value,
                }
            )
                .then((response) => {
                    this.requestId = response.data.id;
                    this.elementsOptions.clientSecret = response.data.key;

                    if (this.$refs.paymentRef) {
                        this.$refs.paymentRef.update();
                    }

                    this.paymentLoading = false;
                })
                .catch((e) => {
                    this.checkGenericError(e);
                    this.paymentLoading = false;
                });
        },
        setPaymentRendered() {
            this.paymentRendered = true;
        },
        actionDownloadFile(article) {
            event.preventDefault();

            Vue.set(this.filesLoading, article, true);
            let self = this;
            let request = new XMLHttpRequest();
            request.open(
                'GET',
                this.apiUrl +
                    '/clickandbuys/' +
                    self.$route.params.clickandbuy +
                    '/article/' +
                    article +
                    '?token=' +
                    this.filesToken +
                    '&order=' +
                    this.$route.query.order,
                true
            );
            request.responseType = 'blob';
            request.onload = function () {
                let blob = request.response;
                let link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = article;
                link.click();
                Vue.set(self.filesLoading, article, false);
            };

            request.send();
        },
        getFiles() {
            const clickAndBuyID =
                this.$route.params.clickandbuy || this.clickAndBuyID;

            let http = axios.create();
            http.get(
                this.apiUrl +
                    '/clickandbuys/' +
                clickAndBuyID +
                    '/files?token=' +
                    this.filesToken +
                    '&order=' +
                    this.$route.query.order
            )
                .then((response) => {
                    this.files = response.data;
                    this.receiveInfosLocale();
                })
                .catch((e) => {
                    this.checkGenericError(e);
                    this.loadingInfos = false;
                    this.filesTokenErr = e;
                    this.receiveInfosLocale();
                });
        },
        receiveInfosLocale() {
            this.loadingInfos = true;

            const clickAndBuyID =
                this.$route.params.clickandbuy || this.clickAndBuyID;

            let http = axios.create();
            http.get(this.apiUrl + '/infos?withclickandbuys=' + clickAndBuyID)
                .then((response) => {
                    let data = response.data;

                    document.title = data.clickAndBuy.name;

                    if (!data.clickAndBuy) {
                        this.routeTo('');
                        return;
                    }

                    this.$store.state.infos = data;

                    try {
                        uuidParse(clickAndBuyID);

                        const target = this.getByID(
                            data.clickAndBuys,
                            clickAndBuyID
                        );

                        if (target) {
                            this.selected = target;

                            if (
                                target.shippingFee &&
                                target.shippingFee > 0.0
                            ) {
                                this.contactFields = [
                                    {
                                        value: '',
                                        valid: false,

                                        name: 'name',
                                        label: 'name',
                                        icon: 'user',
                                        rules: 'required|max:320',
                                        width: 'uk-width-1-2@m uk-width-1-1',
                                    },
                                    {
                                        value: '',
                                        valid: false,

                                        name: 'email',
                                        label: 'emailAddress',
                                        icon: 'mail',
                                        rules: 'required|emailV2',
                                        width: 'uk-width-1-2@m uk-width-1-1',
                                    },

                                    {
                                        value: '',
                                        valid: false,

                                        name: 'street',
                                        label: 'street',
                                        icon: 'home',
                                        rules: 'required|max:320',
                                        width: 'uk-width-1-1',
                                    },
                                    {
                                        value: '',
                                        valid: false,

                                        name: 'zip',
                                        label: 'zip',
                                        icon: 'location',
                                        rules: 'required|numeric|max:320',
                                        width: 'uk-width-1-2@m uk-width-1-1',
                                    },
                                    {
                                        value: '',
                                        valid: false,

                                        name: 'city',
                                        label: 'city',
                                        icon: 'world',
                                        rules: 'required|max:320',
                                        width: 'uk-width-1-2@m uk-width-1-1',
                                    },
                                ];
                            }

                            if (this.selected.price) {
                                this.priceField.value = this.selected.price;
                            }

                            if (this.selected.isPriceVariable) {
                                this.variablePrice = target.price;
                            }

                            if (this.selected.variants) {
                                for (
                                    let i = 0,
                                        j = this.selected.variants.length;
                                    i < j;
                                    i++
                                ) {
                                    this.selected.variants[i].selected = null;

                                    if (
                                        this.selected.variants[i].type ===
                                        'select'
                                    ) {
                                        this.selected.variants[i].selected =
                                            this.selected.variants[
                                                i
                                            ].options[0].name;
                                    }

                                    if (
                                        this.selected.variants[i].type ===
                                        'checkbox'
                                    ) {
                                        for (
                                            let k = 0,
                                                l =
                                                    this.selected.variants[i]
                                                        .options.length;
                                            k < l;
                                            k++
                                        ) {
                                            this.selected.variants[i].options[
                                                k
                                            ].selected = false;
                                        }
                                    }
                                }
                            }

                            this.confirmParams.return_url =
                                window.location.href +
                                this.computedGetClickAndBuyRootLink +
                                '/' +
                                target.id +
                                '/' +
                                this.computedGetClickAndBuyQueryParam;

                            const orderID = this.$route.params.order;
                            if (orderID) {
                                let http = axios.create();
                                http.get(
                                    this.apiUrl +
                                        '/clickandbuys/' +
                                        this.selected.id +
                                        '/orders/' +
                                        orderID
                                )
                                    .then((response) => {
                                        this.contactFields[0].value =
                                            response.data.order.name;
                                        this.contactFields[0].valid = true;
                                        this.contactFields[1].value =
                                            response.data.order.email;
                                        this.contactFields[1].valid = true;
                                        this.contactFields[2].value =
                                            response.data.order.street;
                                        this.contactFields[2].valid = true;
                                        this.contactFields[3].value =
                                            response.data.order.zip;
                                        this.contactFields[3].valid = true;
                                        this.contactFields[4].value =
                                            response.data.order.city;
                                        this.contactFields[4].valid = true;
                                        this.comments =
                                            response.data.order.comments;
                                        this.country =
                                            response.data.order.country;

                                        if (response.data.order.variablePrice) {
                                            this.priceField.value =
                                                response.data.order.variablePrice;
                                        }

                                        this.amountField.value =
                                            response.data.order.amount;

                                        if (response.data.order.variants) {
                                            for (
                                                let k = 0,
                                                    l =
                                                        response.data.order
                                                            .variants.length;
                                                k < l;
                                                k++
                                            ) {
                                                const selectedVariant =
                                                    response.data.order
                                                        .variants[k];

                                                for (
                                                    let i = 0,
                                                        j =
                                                            this.selected
                                                                .variants
                                                                .length;
                                                    i < j;
                                                    i++
                                                ) {
                                                    if (
                                                        this.selected.variants[
                                                            i
                                                        ].id ===
                                                        selectedVariant.variantId
                                                    ) {
                                                        if (
                                                            this.selected
                                                                .variants[i]
                                                                .type ===
                                                            'select'
                                                        ) {
                                                            this.selected.variants[
                                                                i
                                                            ].selected =
                                                                selectedVariant.optionName;
                                                        }

                                                        if (
                                                            this.selected
                                                                .variants[i]
                                                                .type ===
                                                            'checkbox'
                                                        ) {
                                                            this.selected.variants[
                                                                i
                                                            ].options.forEach(
                                                                (option) => {
                                                                    if (
                                                                        option.name ===
                                                                        selectedVariant.optionName
                                                                    ) {
                                                                        option.selected = true;
                                                                    }
                                                                }
                                                            );
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        this.confirmTerms = true;
                                        this.termsValid = true;

                                        if (this.orderSuccess) {
                                            this.awaitScrollTo('success');
                                        } else {
                                            this.requestId =
                                                response.data.order.requestId;
                                            this.elementsOptions.clientSecret =
                                                response.data.key;
                                            this.$refs.paymentRef.update();

                                            this.handleScroll();
                                        }
                                    })
                                    .catch(() => {
                                        if (
                                            target.price &&
                                            target.price > 0.0
                                        ) {
                                            this.orderAgain();
                                            this.postOrderRequestPayment();

                                            this.country =
                                                data.shippingCountries[0];
                                            if (
                                                data.shippingCountries.includes(
                                                    data.country
                                                )
                                            ) {
                                                this.country = data.country;
                                            }
                                        } else {
                                            this.contactFields = [
                                                {
                                                    value: '',
                                                    valid: false,

                                                    name: 'name',
                                                    label: 'name',
                                                    icon: 'user',
                                                    rules: 'required|max:320',
                                                    width: 'uk-width-1-2@m uk-width-1-1',
                                                },
                                                {
                                                    value: '',
                                                    valid: false,

                                                    name: 'email',
                                                    label: 'emailAddress',
                                                    icon: 'mail',
                                                    rules: 'required|emailV2',
                                                    width: 'uk-width-1-2@m uk-width-1-1',
                                                },
                                            ];
                                        }

                                        this.orderSuccess = false;
                                        // this.paymentFailed = false;

                                        this.handleScroll();
                                    });
                            } else {
                                if (target.price && target.price > 0.0) {
                                    this.postOrderRequestPayment();

                                    this.country = data.shippingCountries[0];
                                    if (
                                        data.shippingCountries.includes(
                                            data.country
                                        )
                                    ) {
                                        this.country = data.country;
                                    }
                                } else {
                                    this.contactFields = [
                                        {
                                            value: '',
                                            valid: false,

                                            name: 'name',
                                            label: 'name',
                                            icon: 'user',
                                            rules: 'required|max:320',
                                            width: 'uk-width-1-2@m uk-width-1-1',
                                        },
                                        {
                                            value: '',
                                            valid: false,

                                            name: 'email',
                                            label: 'emailAddress',
                                            icon: 'mail',
                                            rules: 'required|emailV2',
                                            width: 'uk-width-1-2@m uk-width-1-1',
                                        },
                                    ];
                                }

                                this.handleScroll();
                            }
                        } else {
                            this.routeTo('clickandbuys');
                        }
                    } catch (e) {
                        this.routeTo('clickandbuys');
                    }

                    this.loadingInfos = false;
                })
                .catch((e) => {
                    this.checkGenericError(e);
                    this.loadingInfos = false;
                });
        },
        setValidContactFields(valid, fieldKey) {
            this.contactFields[fieldKey].valid = valid;
        },
        setTermsValid(valid) {
            this.termsValid = valid;
        },
        getPriceShipping(clickAndBuy) {
            if (clickAndBuy.shippingFee && clickAndBuy.shippingFee > 0.0) {
                let shippingFee = clickAndBuy.shippingFee;
                if (!clickAndBuy.shippingCostsOnce) {
                    shippingFee *= this.amountField.value;
                }

                return this.getNumberWithCurrency(shippingFee);
            }

            return this.getNumberWithCurrency(0);
        },
        getPriceTotal(clickAndBuy) {
            let shippingPrice = 0;
            if (clickAndBuy.shippingFee && clickAndBuy.shippingFee > 0.0) {
                shippingPrice = clickAndBuy.shippingFee;

                if (!clickAndBuy.shippingCostsOnce) {
                    shippingPrice *= this.amountField.value;
                }
            }

            if (clickAndBuy.price && clickAndBuy.price > 0.0) {
                if (clickAndBuy.isPriceVariable) {
                    return this.getNumberWithCurrency(
                        this.priceField.value +
                            shippingPrice +
                            this.computedOptionsPrice()
                    );
                }

                return this.getNumberWithCurrency(
                    (clickAndBuy.price + this.computedOptionsPrice()) *
                        this.amountField.value +
                        shippingPrice
                );
            }

            return this.$t('free');
        },
        getPrice(clickAndBuy) {
            if (clickAndBuy.price && clickAndBuy.price > 0.0) {
                let suffix = '';
                if (clickAndBuy.isPriceVariable) {
                    suffix = ' ' + this.$t('minimum.price');
                }
                return this.getNumberWithCurrency(clickAndBuy.price) + suffix;
            }

            return this.$t('free');
        },
        getByID(clickandbuys, id) {
            for (let i = 0, j = clickandbuys.length; i < j; i++) {
                const clickandbuy = clickandbuys[i];

                if (clickandbuy.id === id) {
                    return clickandbuy;
                }
            }

            return undefined;
        },
    },
    created() {
        if (
            this.$route.path.includes('lunacart') &&
            this.$route.path.includes('files') &&
            this.$route.query.order &&
            this.$route.query.token
        ) {
            this.filesToken = this.$route.query.token;
            this.getFiles();
            return;
        }

        const paymentStatus = this.$route.query.redirect_status;
        if (
            paymentStatus &&
            (paymentStatus.includes('succeeded') ||
                paymentStatus.includes('processing'))
        ) {
            this.orderSuccess = true;
            this.awaitScrollTo('success');
        }

        this.paymentFailed = paymentStatus && paymentStatus.includes('failed');
        this.receiveInfosLocale();
    },
};
</script>

<style lang="scss" scoped>
.badge {
    font-size: 16px;
    padding: 10px 15px;
    margin-top: -5px;
    margin-right: 5px;
}

.variant-option {
    word-wrap: break-word;
}

.child-description {
    font-size: 14px;
}

.vat {
    font-size: 14px;
    margin-top: 15px;
}

.clickandbuy-title {
    margin-top: 15px;
    color: $site-background;
}

.clickandbuy-media {
    border-radius: 5px;
    max-height: 200px;
}

.clickandbuy-title {
    font-size: 18px;
    font-weight: 500;
    margin-top: 20px;
}

.clickandbuy-text {
    word-break: break-word;
    margin-top: 15px;
    font-weight: 100;
}

.clickandbuy-price {
    margin-top: 20px;
}

.badge {
    font-size: 16px !important;
    padding: 12px 15px !important;
    background-color: $info-color !important;
}

.section-title {
    font-size: 18px;
    font-weight: 500;
    color: $site-background;
    margin-bottom: 10px;
}

.round {
    border-radius: 5px;
}

.paper-text {
    font-weight: 500;
    word-break: break-all;
}

.paper-price {
    font-size: 14px;
}
</style>
